import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import { setFormatLocale } from '@/lib/format'
import ptBR from './locales/pt-BR.json'
import { DEFAULT_LOCALE, LANGUAGE_STORAGE_KEY, SUPPORTED_LOCALES, isLocale, resolveInitialLocale, type Locale } from './locale'

/* Spec PARTNER-001 / PARTNER-005 — i18n do app da OSP nos 5 idiomas do app do padrinho (pt-BR, en-US, es-ES, de-DE,
   ja-JP). Chaves com namespace no primeiro nivel: t('auth.login.submit').
   D-01: so o pt-BR (padrao e fallback) vai no pacote; os outros catalogos sao baixados sob demanda (import()), como no
   app do padrinho. D-02: idioma inicial = o salvo no aparelho, senao o do navegador (se for um dos 5), senao pt-BR.
   Depois do login, o idioma salvo da pessoa no servidor vence (applyAccountLocale). */

export { DEFAULT_LOCALE, SUPPORTED_LOCALES, type Locale } from './locale'

const LOADERS: Record<Exclude<Locale, 'pt-BR'>, () => Promise<{ default: Record<string, unknown> }>> = {
    'en-US': () => import('./locales/en-US.json'),
    'es-ES': () => import('./locales/es-ES.json'),
    'de-DE': () => import('./locales/de-DE.json'),
    'ja-JP': () => import('./locales/ja-JP.json'),
}

/* localStorage pode faltar ou falhar (modo privado, bloqueio do navegador): o app segue sem guardar. */
function readStored(): string | null {
    try {
        return localStorage.getItem(LANGUAGE_STORAGE_KEY)
    } catch {
        return null
    }
}

function store(locale: Locale) {
    try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, locale)
    } catch {
        /* sem armazenamento: vale so nesta aba */
    }
}

async function ensureLocale(locale: Locale): Promise<void> {
    if (locale === 'pt-BR' || i18n.hasResourceBundle(locale, 'translation')) return
    const catalog = await LOADERS[locale]()
    i18n.addResourceBundle(locale, 'translation', catalog.default, true, true)
}

/** Idioma atual da interface (sempre um dos 5). */
export function currentLocale(): Locale {
    return isLocale(i18n.language) ? i18n.language : DEFAULT_LOCALE
}

/* Troca na hora + guarda no aparelho. Salvar no servidor (PATCH /me/message-locale) e de quem chama (D-03). */
export async function changeLocale(locale: Locale): Promise<void> {
    await ensureLocale(locale)
    store(locale)
    if (i18n.language !== locale) await i18n.changeLanguage(locale)
}

/* D-03: depois do login, o idioma salvo da pessoa vence o do aparelho. Falha ao baixar o catalogo: fica como esta. */
export async function applyAccountLocale(locale: Locale): Promise<void> {
    if (locale === currentLocale()) {
        store(locale)
        return
    }
    try {
        await changeLocale(locale)
    } catch {
        /* segue no idioma atual */
    }
}

function applyDocument(lng: string) {
    document.documentElement.lang = lng
    document.title = i18n.t('common.appName')
    setFormatLocale(lng)
}

const navigatorLanguages = typeof navigator === 'undefined' ? [] : navigator.languages?.length ? navigator.languages : [navigator.language ?? '']
const initialLocale = resolveInitialLocale(readStored(), navigatorLanguages)

/* Resolve quando o idioma inicial esta pronto; o main.tsx aguarda antes de renderizar.
   Falha ao baixar o catalogo (offline no 1o acesso) cai no pt-BR em vez de travar o app. */
export const i18nReady: Promise<void> = i18n.use(initReactI18next).init({
    resources: { 'pt-BR': { translation: ptBR } },
    lng: DEFAULT_LOCALE,
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    partialBundledLanguages: true,
    interpolation: { escapeValue: false },
    returnNull: false,
}).then(async () => {
    applyDocument(i18n.language)
    if (initialLocale === DEFAULT_LOCALE) return
    try {
        await ensureLocale(initialLocale)
        await i18n.changeLanguage(initialLocale)
    } catch {
        /* segue em pt-BR */
    }
})

i18n.on('languageChanged', applyDocument)

export default i18n
